import { environment } from 'src/environments/environment';
import { Select, TransactionFilterMode, TYPE_DATE } from '../../interfaces/ui/ui.interface';
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { format } from 'date-fns';
import { DateFilterOptions } from '../../interfaces/api/promotional-tools.interface';
import {
  TRANSACTION_FILTER_MODE_STORAGE_KEY,
  TRANSACTION_FILTER_MODES,
  USER_TYPE,
} from './global.constant';
import { TYPES_USER_PERMISSIONS } from './user-management.constant';
import { User } from '../../interfaces/api/user.interface';
/**
 * Elimina las entradas de un objeto de filtros que son `undefined`, `null`, o coinciden con un valor específico.
 *
 * @param {Object<string, string>} filters - Un objeto donde las claves representan nombres de filtros y los valores son los valores de dichos filtros.
 * @param {string} [value] - Un valor opcional. Si algún filtro tiene este valor, se eliminará del objeto de filtros.
 * @return {Object<string, string>} El objeto de filtros limpio, con las entradas eliminadas si eran `undefined`, `null` o coincidían con el valor especificado.
 */
export function removeEmptyOrSpecificFilters(
  filters: { [key: string]: string },
  value?: string
): { [key: string]: string } {
  Object.keys(filters).forEach((key) => {
    if (filters[key] === undefined || filters[key] === null || filters[key] === value) {
      delete filters[key];
    }
  });
  return filters;
}

/**
 * Compara dos objetos del mismo tipo y devuelve un nuevo objeto que contiene las propiedades de `obj1` cuyos valores son diferentes en `obj2`.
 *
 * @param {T} obj1 - El primer objeto a comparar.
 * @param {T} obj2 - El segundo objeto a comparar.
 * @return {Partial<T>} Un nuevo objeto que contiene las propiedades de `obj1` con los valores correspondientes de `obj2` para aquellas propiedades que son diferentes entre los dos objetos.
 */
export function getDifferingProperties<T extends object>(obj1: T, obj2: T): Partial<T> {
  const differingProperties: Partial<T> = Object.keys(obj1).reduce((result, key) => {
    if (obj1[key as keyof T] !== obj2[key as keyof T]) {
      result[key as keyof T] = obj2[key as keyof T];
    }
    return result;
  }, {} as Partial<T>);

  return differingProperties;
}

export function calculateSum(cells: any[]): string {
  const totalNetGain = cells.reduce((acc, cell) => acc + parseFloat(cell), 0);
  return totalNetGain.toFixed(2);
}

export function mapAndTranslateLabels(
  parametters?: Select[],
  translate?: TranslateService
): Select[] {
  return parametters!.map((item) => ({
    ...item,
    label: translate!.instant(item.label),
  }));
}
export function getDifferingPropertiesArray<T extends object>(obj1: T, obj2: T): Partial<T> {
  const isObject = (obj: any) => obj !== null && typeof obj === 'object';

  function deepDiff(o1: any, o2: any): any {
    if (Array.isArray(o1) && Array.isArray(o2)) {
      return arrayDiffering(o1, o2, deepDiff);
    } else if (isObject(o1) && isObject(o2)) {
      return objectDiffering(o1, o2, deepDiff);
    } else if (o1 !== o2) {
      // Comparamos valores simples (strings, numbers, booleans, etc.)
      return o2;
    }
    return undefined;
  }

  return deepDiff(obj1, obj2) || ({} as Partial<T>);
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Error converting file to base64'));
    reader.readAsDataURL(file);
  });
}

function arrayDiffering(o1: any[], o2: any[], deepDiff: (o1: any, o2: any) => any): any {
  if (o1.length !== o2.length) {
    return o2; // Si los arrays tienen diferente longitud, los consideramos diferentes
  }
  const arrayDiff = o1.map((item, index) => deepDiff(item, o2[index]));
  return arrayDiff.some((diff) => diff !== undefined) ? o2 : undefined;
}

function objectDiffering<T extends Record<string, any>>(
  o1: T,
  o2: T,
  deepDiff: (o1: any, o2: any) => any
): Partial<T> | undefined {
  const isDate = (obj: any) => obj instanceof Date;

  if (isDate(o1) && isDate(o2)) {
    // Comparación específica para fechas
    return o1['getTime']() !== o2['getTime']() ? o2 : undefined;
  }

  // Comparamos otros objetos
  const differingProperties: Partial<T> = {};
  const allKeys = new Set([...Object.keys(o1), ...Object.keys(o2)]);
  allKeys.forEach((key) => {
    const diff = deepDiff(o1[key as keyof T], o2[key as keyof T]);
    if (diff !== undefined) {
      differingProperties[key as keyof T] = o2[key as keyof T]; // Siempre guardamos la propiedad del segundo objeto si hay diferencia
    }
  });

  return Object.keys(differingProperties).length > 0 ? differingProperties : undefined;
}

export const toBoolean = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};

export function calculateDaysInRange(dateRange: Date[], options: DateFilterOptions): number {
  const {
    type,
    selectedYears = [],
    selectedMonths = [],
    selectedDays = [],
    selectWeekDay = [],
  } = options;

  if (type === TYPE_DATE.DAY || type === TYPE_DATE.MONTH || type === TYPE_DATE.YEAR) {
    let filteredDates = [...dateRange];

    if (selectedYears.length > 0) {
      filteredDates = filteredDates.filter((date) => selectedYears.includes(date.getFullYear()));
    }

    if (selectedMonths.length > 0) {
      filteredDates = filteredDates.filter((date) =>
        selectedMonths.includes(format(date, 'MMM').toLowerCase())
      );
    }

    if (selectedDays.length > 0) {
      filteredDates = filteredDates.filter((date) => selectedDays.includes(date.getDate()));
    }

    return filteredDates.length;
  } else {
    let totalWeekDaysCount = 0;

    if (selectWeekDay.length > 0) {
      selectWeekDay.forEach((weekDay) => {
        totalWeekDaysCount += dateRange.filter(
          (date) => format(date, 'EEEE').toLowerCase() === weekDay
        ).length;
      });
      return totalWeekDaysCount;
    }

    // Si no hay días seleccionados, se devuelve todo el rango
    return dateRange.length;
  }
}

export function getSelectedDateDescriptionTranslated(
  selectedDate: {
    selectedYears?: number[];
    selectedMonths?: string[];
    selectedDays?: number[];
    selectWeekDay?: string[];
    type?: string;
  },
  translate: TranslateService
): string {
  const {
    selectedYears = [],
    selectedMonths = [],
    selectedDays = [],
    selectWeekDay = [],
  } = selectedDate;
  const parts: string[] = [];

  if (selectedYears.length > 0) {
    const label = translate.instant('words.selectedYears');
    parts.push(`${label} ${selectedYears.join(', ')}`);
  }

  if (selectedMonths.length > 0) {
    const translatedMonths = selectedMonths.map((m) =>
      translate.instant(`words.${m.toLowerCase()}`)
    );
    const label = translate.instant('words.selectedMonths');
    parts.push(`${label} ${translatedMonths.join(', ')}`);
  }

  if (selectedDays.length > 0) {
    const label = translate.instant('words.selectedDays');
    parts.push(`${label} ${selectedDays.join(', ')}`);
  }

  if (selectWeekDay.length > 0) {
    const translatedWeekDays = selectWeekDay.map((d) =>
      translate.instant(`words.${d.toLowerCase()}`)
    );
    const label = translate.instant('words.selectedWeekDays');
    parts.push(`${label} ${translatedWeekDays.join(', ')}`);
  }

  return parts.join('\n');
}

const MILLISECONDS_IN_MINUTE = 60000;
const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;

/**
 * Construye una etiqueta corta de tiempo transcurrido (`5 min`, `3 h`, `2 d`) a partir de una fecha.
 *
 * @param {string | Date} date - La fecha de origen.
 * @param {string} justNowLabel - Texto ya traducido a usar cuando transcurrió menos de un minuto.
 * @return {string} La etiqueta de tiempo transcurrido.
 */
export function getRelativeTimeLabel(date: string | Date, justNowLabel: string): string {
  const minutes = Math.floor((Date.now() - new Date(date).getTime()) / MILLISECONDS_IN_MINUTE);

  if (minutes < 1) {
    return justNowLabel;
  }

  if (minutes < MINUTES_IN_HOUR) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / MINUTES_IN_HOUR);

  if (hours < HOURS_IN_DAY) {
    return `${hours} h`;
  }

  return `${Math.floor(hours / HOURS_IN_DAY)} d`;
}

/**
 * Construye la etiqueta de acuse de lectura de un mensaje enviado.
 *
 * @param {string | null | undefined} readAt - Fecha de lectura, o nulo si aún no fue leído.
 * @param {string} sentLabel - Texto ya traducido para el estado "enviado".
 * @param {string} seenLabel - Texto ya traducido para el estado "visto".
 * @return {string} La etiqueta de acuse de lectura.
 */
export function getReadReceiptLabel(
  readAt: string | null | undefined,
  sentLabel: string,
  seenLabel: string
): string {
  if (!readAt) {
    return sentLabel;
  }

  return `${seenLabel} ${new Date(readAt).toLocaleString()}`;
}

export function getRefId(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && '_id' in value) {
    return String((value as { _id: unknown })._id);
  }
  return '';
}

export function orderColumnsByName<T extends { name?: string }>(
  columns: T[],
  order: string[]
): T[] {
  const rank = (column: T) => {
    const index = order.indexOf(column.name ?? '');
    return index === -1 ? order.length : index;
  };
  return columns
    .map((column, index) => ({ column, index }))
    .sort((a, b) => rank(a.column) - rank(b.column) || a.index - b.index)
    .map(({ column }) => column);
}

export function readStoredFilterMode(): TransactionFilterMode {
  try {
    return localStorage.getItem(TRANSACTION_FILTER_MODE_STORAGE_KEY) ===
      TRANSACTION_FILTER_MODES.ADVANCED
      ? TRANSACTION_FILTER_MODES.ADVANCED
      : TRANSACTION_FILTER_MODES.SIMPLE;
  } catch {
    return TRANSACTION_FILTER_MODES.SIMPLE;
  }
}

export function storeFilterMode(mode: TransactionFilterMode): void {
  try {
    localStorage.setItem(TRANSACTION_FILTER_MODE_STORAGE_KEY, mode);
  } catch {
    return;
  }
}

export function getInitials(name: string | undefined | null): string {
  return (name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

const normalizeFormValue = (value: any): string =>
  JSON.stringify(value === undefined || value === '' ? null : value);

function hasControlChanged(control: AbstractControl, original: any): boolean {
  return normalizeFormValue(control.getRawValue()) !== normalizeFormValue(original);
}

export function isChangedAndInvalid(control: AbstractControl | null, original: any): boolean {
  if (!control) return false;
  if (control instanceof FormGroup) {
    const childChanged = Object.keys(control.controls).some((key) =>
      isChangedAndInvalid(control.controls[key], original?.[key])
    );
    return childChanged || (!!control.errors && hasControlChanged(control, original));
  }
  if (control instanceof FormArray) return control.invalid && hasControlChanged(control, original);
  return control.invalid && hasControlChanged(control, original);
}
export const canSelectAgentFilter = (profile: User | null | undefined): boolean => {
  const userType = profile?.userType;
  return (
    userType !== TYPES_USER_PERMISSIONS.AGENT &&
    userType !== TYPES_USER_PERMISSIONS.CASHDESK &&
    userType !== TYPES_USER_PERMISSIONS.COLLABORATOR_AGENT &&
    profile?.roleId?.name !== USER_TYPE.AFFILIATE
  );
};
